import React, { useState, useEffect } from 'react';
import { hotseatAPI } from '../services/api';

const HotseatScheduler = ({ group, onUpdate }) => {
  const [hotseats, setHotseats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMonth, setEditingMonth] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [newMonth, setNewMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (group && group.id) {
      fetchHotseats();
    }
  }, [group]);

  const fetchHotseats = async () => {
    try {
      const response = await hotseatAPI.getByGroup(group.id);
      setHotseats(response.data);
    } catch (error) {
      console.error('Error fetching hotseats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHotseat = async (month, memberNames) => {
    try {
      await hotseatAPI.upsert({
        group_id: group.id,
        month: month,
        member_names: memberNames
      });
      
      setEditingMonth(null);
      setSelectedMembers([]);
      await fetchHotseats();
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving hotseat:', error);
    }
  };

  const handleDeleteHotseat = async (month) => {
    if (window.confirm('Are you sure you want to delete this hotseat schedule?')) {
      try {
        await hotseatAPI.delete(group.id, month);
        await fetchHotseats();
        
        if (onUpdate) {
          onUpdate();
        }
      } catch (error) {
        console.error('Error deleting hotseat:', error);
      }
    }
  };

  const startEditing = (hotseat) => {
    setEditingMonth(hotseat.month);
    setSelectedMembers(hotseat.member_names || []);
  };

  const startAddingNew = () => {
    setEditingMonth(newMonth);
    setSelectedMembers([]);
  };

  const cancelEditing = () => {
    setEditingMonth(null);
    setSelectedMembers([]);
  };

  const toggleMemberSelection = (memberName) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberName)) {
        return prev.filter(name => name !== memberName);
      } else {
        return [...prev, memberName];
      }
    });
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const availableMembers = group.members ? group.members.filter(m => m && m.name) : [];

  if (loading) {
    return <div className="animate-pulse bg-gray-200 h-32 rounded"></div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">🎯 Hotseat Schedule</h3>
        <button
          onClick={startAddingNew}
          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
        >
          Add Hotseat
        </button>
      </div>

      {/* Add New Hotseat */}
      {editingMonth === newMonth && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-green-800 mb-3">
            Schedule Hotseat for {getMonthName(newMonth)}
          </h4>
          
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Month
            </label>
            <input
              type="month"
              value={newMonth}
              onChange={(e) => setNewMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Members for Hotseat
            </label>
            <div className="grid grid-cols-2 gap-2">
              {availableMembers.map(member => (
                <label key={member.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.name)}
                    onChange={() => toggleMemberSelection(member.name)}
                    className="mr-2"
                  />
                  <span className="text-sm">{member.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => handleSaveHotseat(newMonth, selectedMembers)}
              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
            >
              Save
            </button>
            <button
              onClick={cancelEditing}
              className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing Hotseats */}
      <div className="space-y-3">
        {hotseats.length > 0 ? (
          hotseats.map(hotseat => (
            <div key={hotseat.month} className="border rounded-lg p-4">
              {editingMonth === hotseat.month ? (
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">
                    Edit Hotseat for {getMonthName(hotseat.month)}
                  </h4>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Members for Hotseat
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {availableMembers.map(member => (
                        <label key={member.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedMembers.includes(member.name)}
                            onChange={() => toggleMemberSelection(member.name)}
                            className="mr-2"
                          />
                          <span className="text-sm">{member.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleSaveHotseat(hotseat.month, selectedMembers)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-800">
                      {getMonthName(hotseat.month)}
                    </h4>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {hotseat.member_names && hotseat.member_names.length > 0 ? (
                        hotseat.member_names.map((name, index) => (
                          <span key={index} className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                            {name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500 italic">No members assigned</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEditing(hotseat)}
                      className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteHotseat(hotseat.month)}
                      className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No hotseat schedules yet.</p>
            <p className="text-sm">Click "Add Hotseat" to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotseatScheduler;